/* eslint-disable no-restricted-syntax */
export const hexToRgba = (hex: string, alpha: number): string => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return hex;
  const r = parseInt(result[1], 16);
  const g = parseInt(result[2], 16);
  const b = parseInt(result[3], 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const hexToRgb = (
  hex: string
): { r: number; g: number; b: number } | null => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
      }
    : null;
};

export const getLuminance = (r: number, g: number, b: number): number => {
  const [rs, gs, bs] = [r, g, b].map(c => {
    c = c / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
};

export const isLightColor = (hexColor: string): boolean => {
  const rgb = hexToRgb(hexColor);
  if (!rgb) return false;

  const luminance = getLuminance(rgb.r, rgb.g, rgb.b);
  return luminance > 0.7; // Threshold for "light" colors
};

export const getContrastRatio = (color1: string, color2: string): number => {
  const rgb1 = hexToRgb(color1);
  const rgb2 = hexToRgb(color2);

  if (!rgb1 || !rgb2) return 0;

  const lum1 = getLuminance(rgb1.r, rgb1.g, rgb1.b);
  const lum2 = getLuminance(rgb2.r, rgb2.g, rgb2.b);

  const brightest = Math.max(lum1, lum2);
  const darkest = Math.min(lum1, lum2);

  return (brightest + 0.05) / (darkest + 0.05);
};

export const getSafeColorForElement = (
  brandColor: string,
  theme: 'light' | 'dark'
): string => {
  if (!brandColor || !hexToRgb(brandColor)) {
    return brandColor;
  }

  // Iteratively adjust the brand color so it maintains 4.5:1 (WCAG AA
  // normal text) against the theme's page background. Applies to any
  // element that renders brand-colored text on the base layer — nav
  // labels, links, badges, chip text. Previously only very-light or
  // very-dark colors were adjusted, missing mid-luminance brand values
  // like #ff9500 (fg 2:1 on #f4f4f5).
  const bg = theme === 'dark' ? '#0d1117' : '#f4f4f5';
  const minRatio = 4.5;
  const maxSteps = 12;
  let current = brandColor;
  for (let step = 0; step < maxSteps; step++) {
    if (getContrastRatio(current, bg) >= minRatio) return current;
    current =
      theme === 'dark' ? lightenColor(current, 8) : darkenColor(current, 8);
  }
  return current;
};

/**
 * Gets the appropriate text color (black or white) for a given background color
 * to ensure WCAG AA contrast ratio (4.5:1 for normal text)
 */
export const getContrastTextColor = (backgroundColor: string): string => {
  const dark = '#111827';
  const light = '#ffffff';
  const rgb = hexToRgb(backgroundColor);
  if (!rgb) return light;

  // Pick whichever of dark/light has the higher contrast against the bg.
  // This avoids the mid-luminance trap where a naive luminance>0.5 heuristic
  // picks white on a brand blue that only reaches ~4.3:1 (below WCAG AA 4.5:1).
  const darkRatio = getContrastRatio(dark, backgroundColor);
  const lightRatio = getContrastRatio(light, backgroundColor);
  return darkRatio >= lightRatio ? dark : light;
};

/**
 * Darkens a hex color by a given percentage
 */
export const darkenColor = (hexColor: string, percent: number): string => {
  const rgb = hexToRgb(hexColor);
  if (!rgb) return hexColor;

  const factor = 1 - percent / 100;
  const r = Math.max(0, Math.round(rgb.r * factor));
  const g = Math.max(0, Math.round(rgb.g * factor));
  const b = Math.max(0, Math.round(rgb.b * factor));

  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};

/**
 * Lightens a hex color by a given percentage
 */
export const lightenColor = (hexColor: string, percent: number): string => {
  const rgb = hexToRgb(hexColor);
  if (!rgb) return hexColor;

  const factor = percent / 100;
  const r = Math.min(255, Math.round(rgb.r + (255 - rgb.r) * factor));
  const g = Math.min(255, Math.round(rgb.g + (255 - rgb.g) * factor));
  const b = Math.min(255, Math.round(rgb.b + (255 - rgb.b) * factor));

  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};

/**
 * Checks if a brand color would be problematic as a button background
 * Returns true if the color is too light/dark for good visibility
 */
export const isBrandColorProblematic = (
  brandColor: string,
  theme: 'light' | 'dark'
): boolean => {
  const rgb = hexToRgb(brandColor);
  if (!rgb) return false;

  const luminance = getLuminance(rgb.r, rgb.g, rgb.b);

  // Very light colors (close to white) are problematic in light theme
  if (theme === 'light' && luminance > 0.85) {
    return true;
  }

  // Very dark colors (close to black) are problematic in dark theme
  if (theme === 'dark' && luminance < 0.15) {
    return true;
  }

  return false;
};

/**
 * Ensures a brand color has sufficient contrast when used as a background
 * If the original color is too light/dark, it adjusts it to maintain brand identity
 * while ensuring visibility
 *
 * ONLY use this for backgrounds (buttons, pills, badges) where the brand color
 * would be invisible. For borders, accents, and highlights, use the original brand color.
 */
export const getBrandColorForBackground = (
  brandColor: string,
  theme: 'light' | 'dark'
): string => {
  if (!brandColor || !hexToRgb(brandColor)) {
    return brandColor;
  }

  const rgb = hexToRgb(brandColor);
  if (!rgb) return brandColor;

  const luminance = getLuminance(rgb.r, rgb.g, rgb.b);

  // For very light colors (luminance > 0.85), darken them moderately
  // to maintain brand identity while ensuring visibility
  if (luminance > 0.85) {
    return darkenColor(brandColor, 40);
  }

  // For very dark colors (luminance < 0.15) in dark theme, lighten them
  if (theme === 'dark' && luminance < 0.15) {
    return lightenColor(brandColor, 40);
  }

  return brandColor;
};

/**
 * Given a solid background color, returns a version darkened just enough
 * (up to `maxSteps`) that white text achieves the requested WCAG contrast ratio.
 * Used for primary-button-style surfaces where the visual expectation is
 * "dark bg + white text".
 */
export const ensureBgForWhiteText = (
  backgroundColor: string,
  minRatio: number = 4.5,
  maxSteps: number = 6
): string => {
  let current = backgroundColor;
  for (let step = 0; step < maxSteps; step++) {
    if (getContrastRatio('#ffffff', current) >= minRatio) return current;
    current = darkenColor(current, 8);
  }
  return current;
};

/**
 * Iteratively darkens a color until it achieves WCAG AA (4.5:1) against a
 * given background. Use for brand-colored TEXT rendered on tinted/soft
 * backgrounds like badge pills where the effective bg is close to the page
 * bg but slightly warmer/cooler from the brand tint. Falls back to the last
 * step if the target can't be reached (e.g., yellow on yellow).
 */
export const ensureTextOnBg = (
  textColor: string,
  bgColor: string,
  minRatio: number = 4.5,
  maxSteps: number = 12
): string => {
  let current = textColor;
  for (let step = 0; step < maxSteps; step++) {
    if (getContrastRatio(current, bgColor) >= minRatio) return current;
    current = darkenColor(current, 8);
  }
  return current;
};
